import {
    endOfDay,
    endOfMonth,
    endOfYear,
    isFirstDayOfMonth,
    isLastDayOfMonth,
    isSameDay,
    isSameMonth,
    isSameYear,
    startOfDay,
    startOfMonth,
    startOfYear
} from 'date-fns';

import { FilterDateType } from './filter-date-type.entity';
import { FilterDateValue, FilterDateValuePart } from './filter-date-value.entity';

export class FilterDate {
    private static readonly RANGE_QUERY_PARAM: string = 'TO';
    private static readonly TIME_QUERY_PARAM: string = 'T';
    private static readonly WILDCARD_REST_QUERY_PARAM: string = '*';

    /**
     *  Matches:
     *  [2020-12-31T12:12:12.000Z TO 2020-05-12T14:14:14Z]
     *  {2020-12-31T12:12:12.00Z TO 2020-05-12T14:14:14Z]
     *  [2020-12-31T12:12:12.0Z TO 2020-05-12T14:14:14.000Z]
     *  [2020-12-31T12:12:12.0Z TO 2020-05-12T14:14:14.000000Z]
     *  {2020-12-31T12:12:12Z TO 2020-05-12T14:14:14Z]
     *  {2020-12-31T12:12:12.0Z TO *]
     *  {* TO 2020-45-12T14:14:14Z]
     *
     */
    private static readonly RANGE_INPUT: RegExp = new RegExp(
        '(\\[|{)((\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d{0,3})?Z)|\\*) TO ((\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(\\.\\d{0,6})?Z)|\\*)(\\]|\\})'
    );

    /**
     *  Matches:
     *  2020-12-31T12:12:12.000000Z
     *  2020-12-31T12:12:12.000Z
     *  2020-12-31T12:12:12.00Z
     *  2020-12-31T12:12:12.0Z
     *  2020-12-31T12:12:12Z
     *
     */
    private static readonly SPECIFIC_INPUT: RegExp = new RegExp(
        '(\\d{4}-\\d{2}-\\d{2}T\\d{2}:\\d{2}:\\d{2}(.\\d{1,6})?Z)'
    );

    /**
     *  Matches:
     *  [,],{,} and spaces
     */
    private static readonly RANGE_SYMBOLS_AND_SPACES: RegExp = new RegExp('\\]|\\}|\\{|\\[|\\s', 'g');

    readonly value: FilterDateValue;
    readonly filterDateType: FilterDateType = FilterDateType.SPECIFIC;

    constructor(value?: FilterDateValue, type?: FilterDateType) {
        this.value = value || {};
        this.filterDateType = type || this.determineFilterTypeFromValue();
    }

    // #region QueryPartString => FilterDateValue
    public static fromQueryPartString(queryPartString: string): FilterDate {
        if (!queryPartString.match(this.RANGE_INPUT) && !queryPartString.match(this.SPECIFIC_INPUT)) {
            const errorMessage = `FilterDate: Invalid input date "${queryPartString}". Please provide dates in following formats: YYYY-MM-DDThh:mm:ss.SSSSSSZ or [YYYY-MM-DDThh:mm:ss.SSSSSSZ TO YYYY-MM-DDThh:mm:ss.SSSSSSZ]`;
            console.error(errorMessage);
            throw new Error(errorMessage);
        }

        const splitRangQueryPartString: string[] = queryPartString
            .replace(this.RANGE_SYMBOLS_AND_SPACES, '')
            .split(this.RANGE_QUERY_PARAM);

        const fromQueryPartDateString: string = splitRangQueryPartString[0];
        const toQueryPartDateString: string | undefined = splitRangQueryPartString[1];

        const filterDateValue: FilterDateValue = this.isSpecificDateRange(
            fromQueryPartDateString,
            toQueryPartDateString
        )
            ? this.getSpecificFilterDateValueFromQueryPart(fromQueryPartDateString, toQueryPartDateString)
            : this.getRangedFilterDateValueFromQueryPart(fromQueryPartDateString, toQueryPartDateString);

        return new FilterDate(filterDateValue);
    }

    private static isSpecificDateRange(fromQueryPartDateString: string, toQueryPartDateString?: string): boolean {
        if (!toQueryPartDateString) {
            return true;
        }

        const startDate: Date = new Date(fromQueryPartDateString);
        const endDate: Date = new Date(toQueryPartDateString);

        return (
            this.isFullDayRange(startDate, endDate) ||
            this.isFullMonthRange(startDate, endDate) ||
            this.isFullYearRange(startDate, endDate)
        );
    }

    private static isFullDayRange(startDate: Date, endDate: Date): boolean {
        return isSameDay(startDate, endDate);
    }

    private static isFullMonthRange(startDate: Date, endDate: Date): boolean {
        const isStartOfMonth: boolean = isFirstDayOfMonth(startDate);
        const isEndOfMonth: boolean = isLastDayOfMonth(endDate);

        return isStartOfMonth && isEndOfMonth && isSameMonth(startDate, endDate);
    }

    private static isFullYearRange(startDate: Date, endDate: Date): boolean {
        const isStartOfYear: boolean = isSameDay(startOfYear(startDate), startDate);
        const isEndOfYear: boolean = isSameDay(endOfYear(endDate), endDate);

        return isStartOfYear && isEndOfYear && isSameYear(startDate, endDate);
    }

    private static getSpecificFilterDateValueFromQueryPart(
        fromQueryPartDateString: string,
        toQueryPartDateString?: string
    ): FilterDateValue {
        let specificFilterDateValue: FilterDateValuePart | undefined;
        if (!toQueryPartDateString) {
            specificFilterDateValue = this.getFilterDateValuePartFromQueryPartString(fromQueryPartDateString);
        } else {
            specificFilterDateValue = this.getSpecificFilterDataValuePartFromQueryPartStringRange(
                fromQueryPartDateString,
                toQueryPartDateString
            );
        }

        const filterDateValue: FilterDateValue = {
            Specific: specificFilterDateValue
        };

        return filterDateValue;
    }

    private static getSpecificFilterDataValuePartFromQueryPartStringRange(
        fromQueryPartDateString: string,
        toQueryPartDateString: string
    ): FilterDateValuePart {
        const startDate: Date = new Date(fromQueryPartDateString);
        const endDate: Date = new Date(toQueryPartDateString);

        const isFullDay: boolean = this.isFullDayRange(startDate, endDate);
        const isFullMonth: boolean = this.isFullMonthRange(startDate, endDate);

        return {
            Year: startDate.getFullYear(),
            ...((isFullMonth || isFullDay) && { Month: startDate.getMonth() + 1 }),
            ...(isFullDay && { Day: startDate.getDate() })
        };
    }

    private static getRangedFilterDateValueFromQueryPart(
        fromQueryPartDateString: string,
        toQueryPartDateString: string
    ): FilterDateValue {
        const hasRangedFromDate = !this.isWildCard(fromQueryPartDateString);
        const hasRangedToDate = !this.isWildCard(toQueryPartDateString);

        const filterDateValue: FilterDateValue = {
            Range: {
                ...(hasRangedFromDate && {
                    From: this.getFilterDateValuePartFromQueryPartString(fromQueryPartDateString)
                }),
                ...(hasRangedToDate && {
                    To: this.getFilterDateValuePartFromQueryPartString(toQueryPartDateString)
                })
            }
        };

        return filterDateValue;
    }

    private static isWildCard(queryPartString: string): boolean {
        return queryPartString === FilterDate.WILDCARD_REST_QUERY_PARAM;
    }

    private static getFilterDateValuePartFromQueryPartString(queryPartString: string): FilterDateValuePart {
        const date: Date = new Date(queryPartString);

        return {
            Year: date.getFullYear(),
            Month: date.getMonth() + 1,
            Day: date.getDate()
        };
    }
    // #endregion

    // #region FilterDateValue => QueryPartString
    private valueToQueryPartString(filterDateValue: FilterDateValue): string {
        const queryPartString: string =
            this.filterDateType === FilterDateType.SPECIFIC
                ? this.createSpecificQueryPartString(filterDateValue)
                : this.createRangeQueryPartString(filterDateValue);
        return queryPartString;
    }

    private createSpecificQueryPartString(filterDateValue: FilterDateValue): string {
        const specificDate: FilterDateValuePart | undefined = filterDateValue.Specific;
        const containsDateInfo = !!specificDate?.Year || !!specificDate?.Month || !!specificDate?.Day;

        const startDate: Date | null =
            containsDateInfo && specificDate ? this.getDateFromFilterDateValuePart(specificDate, true) : null;
        const endDate: Date | null =
            containsDateInfo && specificDate ? this.getDateFromFilterDateValuePart(specificDate, false) : null;

        return this.getDateRangeQueryPartString(startDate, endDate);
    }

    private createRangeQueryPartString(filterDateValue: FilterDateValue): string {
        const hasRangedFromDate = !!(filterDateValue.Range && filterDateValue.Range.From);
        const hasRangedToDate = !!(filterDateValue.Range && filterDateValue.Range.To);

        const startDate: Date | null = hasRangedFromDate
            ? this.getDateFromFilterDateValuePart(filterDateValue.Range!.From!, true)
            : null;
        const endDate: Date | null = hasRangedToDate
            ? this.getDateFromFilterDateValuePart(filterDateValue.Range!.To!, false)
            : null;

        return this.getDateRangeQueryPartString(startDate, endDate);
    }

    private getDateFromFilterDateValuePart(valuepart: FilterDateValuePart, isStartDate: boolean): Date {
        return isStartDate
            ? this.convertToStartDate(valuepart.Year, valuepart.Month, valuepart.Day)
            : this.convertToEndDate(valuepart.Year, valuepart.Month, valuepart.Day);
    }

    private convertToStartDate(year: number, month: number | null = null, day: number | null = null): Date {
        let startDate: Date;

        if (month !== null && day !== null) {
            const date: Date = new Date(Date.UTC(year, month - 1, day));
            startDate = startOfDay(date);
        } else if (month !== null && day === null) {
            const date: Date = new Date(Date.UTC(year, month - 1));
            startDate = startOfMonth(date);
        } else {
            const date: Date = new Date(Date.UTC(year, 0));
            startDate = startOfYear(date);
        }

        // By default year 1-99 will be converted to 1901-1999
        // setFullYear will set the correct year
        startDate.setFullYear(year);
        return startDate;
    }

    private convertToEndDate(year: number, month: number | null = null, day: number | null = null): Date {
        let endDate: Date;

        if (month !== null && day !== null) {
            const date: Date = new Date(Date.UTC(year, month - 1, day));
            endDate = endOfDay(date);
        } else if (month !== null && day === null) {
            const date: Date = new Date(Date.UTC(year, month - 1));
            endDate = endOfMonth(date);
        } else {
            const date: Date = new Date(Date.UTC(year, 0));
            endDate = endOfYear(date);
        }

        // By default year 1-99 will be converted to 1901-1999
        // setFullYear will set the correct year
        endDate.setFullYear(year);
        return endDate;
    }

    private getDateRangeQueryPartString(startDate: Date | null, endDate: Date | null): string {
        const startDateString: string = startDate ? this.dateToString(startDate) : FilterDate.WILDCARD_REST_QUERY_PARAM;
        const endDateString: string = endDate ? this.dateToString(endDate) : FilterDate.WILDCARD_REST_QUERY_PARAM;

        return `[${startDateString} TO ${endDateString}]`;
    }

    private dateToString(date: Date): string {
        return date.toISOString();
    }
    // #endregion

    private determineFilterTypeFromValue(): FilterDateType {
        if (
            this.value.Range &&
            ((this.value.Range.From && this.value.Range.From.Year) || (this.value.Range.To && this.value.Range.To.Year))
        ) {
            return FilterDateType.RANGE;
        } else {
            return FilterDateType.SPECIFIC;
        }
    }

    get queryPartString(): string {
        return this.valueToQueryPartString(this.value);
    }
}
